import { useCallback, useEffect, useRef, useState } from 'react';
import type { EntriesResponse, Entry } from '@ozone/shared';
import { NotAuthorized } from './NotAuthorized';
import { IdentityLinksView } from './IdentityLinksView';
import { TeamView, type Me } from './TeamView';
import { getDevOverride, type DevOverride } from './devOverride';
import { simulatedContributorMe, type PreviewTarget } from './previewIdentity';
import {
  CATEGORY_ORDER,
  filterCurrentMonth,
  groupBySubmitter,
} from './compose';
import {
  applyFilters,
  defaultFilterState,
  distinctSubmitters,
  isDefaultFilterState,
  monthLabelFor,
  monthOptions,
  parseFilters,
  serializeFilters,
  type FilterState,
} from './filters';
import { patchIncludeInMbr, patchMbrDraft, patchMbrPeriod, postRegenerate } from './api';
import { filterSelected, tally } from './tally';
import { TopBar } from './components/TopBar';
import { FilterBar } from './components/FilterBar';
import { TallyBar } from './components/TallyBar';
import { PageHeader } from './components/PageHeader';
import { GenerateMbrButton } from './components/GenerateMbrButton';
import { UserTokensPage } from './components/UserTokensPage';
import { SubmitterCard } from './components/SubmitterCard';
import { PreviewContributorBar } from './components/PreviewContributorBar';
import { LoadingState } from './components/states/LoadingState';
import { ErrorState } from './components/states/ErrorState';
import { EmptyState } from './components/states/EmptyState';
import { NoMatchState } from './components/states/NoMatchState';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; entries: Entry[] }
  | { kind: 'error'; message: string };

/**
 * AOZONE-32: how often we quietly refetch /api/entries while any draft is
 * still 'pending' (the Rewriter Lambda is generating it asynchronously).
 */
export const DRAFT_POLL_MS = 3000;

/**
 * AOZONE-32: merge a freshly-fetched entry list into the current one without
 * clobbering unsaved admin edits. For each existing entry we adopt the
 * incoming row ONLY if our copy was still pending (we were waiting on it);
 * otherwise we keep our copy untouched. Incoming entries we've never seen are
 * appended.
 */
function mergeEntries(prev: Entry[], incoming: Entry[]): Entry[] {
  const incomingById = new Map(incoming.map((e) => [e.entryId, e]));
  const merged = prev.map((p) => {
    const next = incomingById.get(p.entryId);
    if (!next) return p;
    return p.mbrDraftStatus === 'pending' ? next : p;
  });
  const seen = new Set(prev.map((p) => p.entryId));
  for (const e of incoming) {
    if (!seen.has(e.entryId)) merged.push(e);
  }
  return merged;
}

/**
 * Admin SPA. Fetches `/api/entries` on mount and renders the current month's
 * submissions grouped by submitter then by canonical category order.
 *
 * AOZONE-37: visual redesign — Tailwind + shadcn/ui, single-column layout,
 * neutral slate palette, modern type. Right-pane MBR Draft placeholder
 * (AOZONE-31) has been dropped; WS3-2 will reintroduce a two-pane layout
 * when the draft editor ships.
 */
export function App() {
  const isNotAuthorized = window.location.pathname === '/notauthorized';
  // AOZONE-85: admin-only identity-links observability view, reached via
  // `?view=identity-links`. Orthogonal to the pathname-based notauthorized
  // check; the entries machinery below is skipped when this is active.
  const isIdentityLinksView =
    new URLSearchParams(window.location.search).get('view') === 'identity-links';
  // AOZONE-100: dedicated MCP token page. Path-based (relies on the CloudFront
  // 404→index.html SPA fallback for deep-link/refresh). The entries machinery is
  // skipped for this route; `me` is still fetched so the page renders against a
  // known identity rather than an unresolved one (AOZONE-102: no role gate).
  const isUserTokensView = window.location.pathname === '/user/tokens';
  // Pin "now" for the lifetime of the mount so the current-month default is
  // computed consistently across parse/serialize/isDefault/monthOptions — a
  // fresh `new Date()` per call could disagree across a midnight-UTC boundary.
  const nowRef = useRef(new Date());
  // AOZONE-91: dev/preview override (DEV-only; compiled out of prod). When
  // active it stubs the identity and seeds entries so TeamView renders without
  // a real bridge login. Lazily computed once (an `undefined` sentinel vs the
  // `null` "inactive" result) so the seed isn't rebuilt on every render.
  const devRef = useRef<DevOverride | null | undefined>(undefined);
  if (devRef.current === undefined) {
    devRef.current = getDevOverride(window.location.search, nowRef.current);
  }
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [me, setMe] = useState<Me | null>(null);
  // AOZONE-100: distinguishes "still fetching /api/auth/me" from "resolved to
  // null" (401/failure). The tokens route holds a loading state on this so it
  // never renders against an identity that isn't known yet.
  const [meResolved, setMeResolved] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [filters, setFilters] = useState<FilterState>(() =>
    parseFilters(window.location.search, { now: nowRef.current }),
  );
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  // AOZONE-115: "Preview contributor view". The identity being previewed, and
  // the admin's own filter state stashed while it is active.
  //
  // React state ONLY — never the URL, localStorage or a module-level variable.
  // Three reasons, in the ticket's own terms: a `preview=` query token would be
  // reload-durable AND link-shareable, i.e. a URL that drops an arbitrary
  // recipient into the contributor shell; `filters` are parsed from the URL
  // synchronously on mount while `me` resolves asynchronously, so a URL-sourced
  // preview would be readable before the role is known and an unknown role
  // could render the contributor shell — the one direction the fail-open rule
  // below forbids; and ephemerality IS the safety property, since a reload
  // always returns the admin to their real role.
  //
  // The cost, stated rather than glossed: entering the preview resets `filters`
  // and the URL-sync effect above replaceStates them, so the admin's own
  // `?sub=…&q=…` is erased from the URL (and from history — replaceState leaves
  // no back-button recovery). Exiting restores them from `adminFilters` below,
  // but RELOADING mid-preview lands on the admin dashboard with DEFAULT filters
  // rather than the ones they had. Lossy, never role-confusable; that is the
  // trade, and it is the right way round.
  //
  // The synthesized identity is stored ALONGSIDE the target, built once in the
  // selection handler below rather than in the render body: constructing it can
  // throw (an empty slackUserId is the own/all bug, not a degraded identity),
  // and this SPA has no ErrorBoundary, so a throw during render would blank the
  // page. In a click handler it costs one failed click instead.
  const [previewAs, setPreviewAs] = useState<{ target: PreviewTarget; me: Me } | null>(
    null,
  );
  const [adminFilters, setAdminFilters] = useState<FilterState | null>(null);

  // Keep the URL query string in sync with the active filters so reloads and
  // shared links preserve state. replaceState avoids polluting browser history.
  useEffect(() => {
    // Don't clobber `?view=identity-links` (or the /user/tokens path) with the
    // filter query string.
    if (isIdentityLinksView || isUserTokensView) return;
    const qs = serializeFilters(filters, nowRef.current);
    window.history.replaceState(
      null,
      '',
      qs ? `?${qs}` : window.location.pathname,
    );
  }, [filters, isIdentityLinksView, isUserTokensView]);

  useEffect(() => {
    if (isNotAuthorized || isIdentityLinksView || isUserTokensView) return;
    // AOZONE-91: dev override serves seed entries instead of hitting the API
    // (and so never triggers the 401→login redirect locally).
    if (devRef.current) {
      setState({ kind: 'ok', entries: devRef.current.entries });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    fetch('/api/entries')
      .then(async (res) => {
        if (res.status === 401) {
          window.location.assign('/api/auth/login');
          return null;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as EntriesResponse;
      })
      .then((body) => {
        if (body === null) return;
        if (!cancelled) setState({ kind: 'ok', entries: body.entries });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isNotAuthorized, isIdentityLinksView, isUserTokensView, reloadKey]);

  // Best-effort identity probe. We tolerate any failure silently — the TopBar
  // renders a generic affordance if `me` stays null, and an unknown role falls
  // back to the admin UI (never TeamView). AOZONE-91: capture role +
  // slackUserId so the SPA can pick read-only vs editable.
  useEffect(() => {
    if (isNotAuthorized || isIdentityLinksView) return;
    if (devRef.current) {
      setMe(devRef.current.me);
      setMeResolved(true);
      return;
    }
    let cancelled = false;
    fetch('/api/auth/me')
      .then(async (res) => {
        if (!res.ok) return null;
        return (await res.json()) as Me;
      })
      .then((body) => {
        if (cancelled) return;
        if (body) setMe(body);
        // AOZONE-100: mark resolved on EVERY terminal path (including a 401/
        // null body), otherwise the tokens route's loading gate never clears.
        setMeResolved(true);
      })
      .catch(() => {
        if (!cancelled) setMeResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isNotAuthorized, isIdentityLinksView]);

  // AOZONE-32: quiet auto-poll while any draft is still generating. We do NOT
  // reuse reloadKey (that flashes the loading state and unmounts editors).
  // Instead we refetch silently and merge, preserving unsaved admin edits.
  const anyPending =
    state.kind === 'ok' &&
    state.entries.some((e) => e.mbrDraftStatus === 'pending');
  useEffect(() => {
    if (isNotAuthorized || isIdentityLinksView || isUserTokensView || !anyPending) return;
    let cancelled = false;
    const quietRefetch = () => {
      fetch('/api/entries')
        .then(async (res) => {
          if (!res.ok) return null;
          return (await res.json()) as EntriesResponse;
        })
        .then((body) => {
          if (cancelled || !body) return;
          setState((prev) =>
            prev.kind !== 'ok'
              ? prev
              : { ...prev, entries: mergeEntries(prev.entries, body.entries) },
          );
        })
        .catch(() => {
          /* swallow — a transient poll failure is harmless */
        });
    };
    const id = setInterval(quietRefetch, DRAFT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isNotAuthorized, isIdentityLinksView, isUserTokensView, anyPending]);

  const onRetry = useCallback(() => setReloadKey((k) => k + 1), []);

  // AOZONE-115. A real contributor's session begins at the default filter
  // state, so entering the preview resets to it — otherwise an admin with an
  // active submitter/search filter would preview a view no contributor could
  // ever see (often an empty one, which reads as a broken feature rather than
  // as a leaked filter). The month is the one dimension carried across: the
  // picker's universe is month-scoped, so the admin explicitly chose it.
  // Exiting restores exactly what they had.
  const enterPreview = useCallback(
    (target: PreviewTarget) => {
      // `prev ?? filters`: only the FIRST entry stashes. Re-entering without
      // exiting is unreachable today (the active banner renders no picker), but
      // an unconditional stash would overwrite the admin's real filters with
      // the preview's reset ones — irrecoverably — the moment anyone adds a
      // submitter switcher.
      setAdminFilters((prev) => prev ?? filters);
      setFilters({ ...defaultFilterState(nowRef.current), month: filters.month });
      setPreviewAs({ target, me: simulatedContributorMe(target.submitterId) });
    },
    [filters],
  );
  const exitPreview = useCallback(() => {
    setFilters(adminFilters ?? defaultFilterState(nowRef.current));
    setAdminFilters(null);
    setPreviewAs(null);
  }, [adminFilters]);

  // AOZONE-34: optimistic toggle — flip locally first, persist, revert on failure.
  const onToggleInclude = useCallback((entryId: string, next: boolean) => {
    setState((prev) =>
      prev.kind !== 'ok'
        ? prev
        : {
            ...prev,
            entries: prev.entries.map((e) =>
              e.entryId === entryId ? { ...e, includeInMbr: next } : e,
            ),
          },
    );
    patchIncludeInMbr(entryId, next).catch(() => {
      setState((prev) =>
        prev.kind !== 'ok'
          ? prev
          : {
              ...prev,
              entries: prev.entries.map((e) =>
                e.entryId === entryId ? { ...e, includeInMbr: !next } : e,
              ),
            },
      );
    });
  }, []);

  // AOZONE-32: optimistic draft save — flip locally to a ready draft first,
  // persist, and revert to the captured prior entry on failure. We rethrow so
  // the DraftEditor keeps its dirty state and the admin can retry.
  const onSaveDraft = useCallback((entryId: string, md: string): Promise<void> => {
    let prior: Entry | undefined;
    setState((prev) => {
      if (prev.kind !== 'ok') return prev;
      return {
        ...prev,
        entries: prev.entries.map((e) => {
          if (e.entryId !== entryId) return e;
          prior = e;
          return {
            ...e,
            mbrDraftMd: md,
            mbrDraftStatus: 'ready',
            mbrDraftUpdatedAt: new Date().toISOString(),
          };
        }),
      };
    });
    return patchMbrDraft(entryId, md).catch((err: unknown) => {
      setState((prev) =>
        prev.kind !== 'ok' || !prior
          ? prev
          : {
              ...prev,
              entries: prev.entries.map((e) => (e.entryId === entryId ? prior! : e)),
            },
      );
      throw err;
    });
  }, []);

  // AOZONE-59: optimistic mbrPeriod save — flip locally and revert to the
  // captured prior entry on failure. Rethrow so DraftEditor keeps the period
  // dirty so the admin can retry.
  const onSavePeriod = useCallback((entryId: string, mbrPeriod: string): Promise<void> => {
    let prior: Entry | undefined;
    setState((prev) => {
      if (prev.kind !== 'ok') return prev;
      return {
        ...prev,
        entries: prev.entries.map((e) => {
          if (e.entryId !== entryId) return e;
          prior = e;
          return { ...e, mbrPeriod };
        }),
      };
    });
    return patchMbrPeriod(entryId, mbrPeriod).catch((err: unknown) => {
      setState((prev) =>
        prev.kind !== 'ok' || !prior
          ? prev
          : {
              ...prev,
              entries: prev.entries.map((e) => (e.entryId === entryId ? prior! : e)),
            },
      );
      throw err;
    });
  }, []);

  // AOZONE-35: optimistic regenerate — flip the entry to 'pending' locally
  // (which turns on the auto-poll above), then POST. On failure, revert to the
  // captured prior status and rethrow so the DraftEditor can surface the error.
  const onRegenerate = useCallback((entryId: string): Promise<void> => {
    let priorStatus: Entry['mbrDraftStatus'];
    setState((prev) => {
      if (prev.kind !== 'ok') return prev;
      return {
        ...prev,
        entries: prev.entries.map((e) => {
          if (e.entryId !== entryId) return e;
          priorStatus = e.mbrDraftStatus;
          return { ...e, mbrDraftStatus: 'pending' };
        }),
      };
    });
    return postRegenerate(entryId).catch((err: unknown) => {
      setState((prev) =>
        prev.kind !== 'ok'
          ? prev
          : {
              ...prev,
              entries: prev.entries.map((e) =>
                e.entryId === entryId ? { ...e, mbrDraftStatus: priorStatus } : e,
              ),
            },
      );
      throw err;
    });
  }, []);

  if (isNotAuthorized) return <NotAuthorized />;
  if (isIdentityLinksView) return <IdentityLinksView />;

  // AOZONE-100 / AOZONE-102: the /user/tokens page is self-service — every
  // signed-in user manages their own MCP tokens, so there is no role gate here.
  // We still hold a loading state until `/api/auth/me` resolves so the page
  // doesn't render against an unknown identity (and flash the wrong email); the
  // PAT endpoints scope every mint/list/revoke to the caller's session email
  // server-side, which is the real authz boundary.
  if (isUserTokensView) {
    if (!meResolved) {
      return (
        <div className="min-h-screen bg-slate-50 text-slate-900">
          <TopBar userEmail={me?.email ?? null} />
          <LoadingState />
        </div>
      );
    }
    return <UserTokensPage userEmail={me?.email ?? null} />;
  }

  const now = nowRef.current;

  // AOZONE-91: contributors get the compact read-only TeamView; admins (and any
  // unknown/failed role) get the existing edit UI unchanged. UI gating is UX
  // only — the bundle is public and there is no contributor write path.
  // AOZONE-102: contributors get the "MCP tokens" link too — PAT management is
  // self-service, so both shells surface it.
  if (me?.role === 'contributor') {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <TopBar userEmail={me.email ?? null} showTokensLink />
        <TeamView
          entries={state.kind === 'ok' ? state.entries : []}
          me={me}
          filters={filters}
          onChange={setFilters}
          onSearchChange={(q) => setFilters((f) => ({ ...f, search: q }))}
          now={now}
          loading={state.kind === 'loading'}
          error={state.kind === 'error' ? state.message : null}
          onRetry={onRetry}
        />
      </div>
    );
  }

  const monthLabel = monthLabelFor(filters.month);
  // Submitter universe is scoped to the selected month (so the dropdown only
  // offers people who actually submitted that month), independent of the
  // other active filters.
  const monthEntries =
    state.kind === 'ok' ? filterCurrentMonth(state.entries, filters.month) : [];
  const submitters = distinctSubmitters(monthEntries);

  // AOZONE-115. Gated on `role === 'admin'` EXPLICITLY, not on `!== 'contributor'`:
  // the fail-open note below says an unknown/failed role falls open to the admin
  // UI, and that must never come with a route into a spoofed contributor
  // identity. Unknown role ⇒ admin UI, no preview control.
  const previewing = me?.role === 'admin' ? previewAs : null;

  if (previewing) {
    // The preview is its own shell, so "no admin mutation controls are
    // reachable" (AC4) holds structurally — those components are never mounted
    // — rather than by gating each control.
    //
    // TopBar carries the ADMIN's real email, deliberately: TeamView never reads
    // `me.email`, and the honest reading is that the chrome shows who you are
    // while the banner names who you are looking at. Do NOT spoof it.
    //
    // And do NOT reword TeamView's "Showing only your entries" caption to name
    // the previewed submitter. It reads slightly oddly to an admin, but the
    // whole point is that the contributor's view renders UNCHANGED; rewording
    // it would break the AC2 fidelity property. The banner is where the
    // disambiguation belongs.
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 ring-4 ring-inset ring-amber-400">
        <TopBar userEmail={me?.email ?? null} showTokensLink />
        <PreviewContributorBar
          submitters={submitters}
          previewing={previewing.target}
          onSelect={enterPreview}
          onExit={exitPreview}
        />
        <TeamView
          entries={state.kind === 'ok' ? state.entries : []}
          me={previewing.me}
          filters={filters}
          onChange={setFilters}
          onSearchChange={(q) => setFilters((f) => ({ ...f, search: q }))}
          now={now}
          loading={state.kind === 'loading'}
          error={state.kind === 'error' ? state.message : null}
          onRetry={onRetry}
        />
      </div>
    );
  }

  const filtered =
    state.kind === 'ok' ? applyFilters(state.entries, filters) : [];
  // Tally on the categorized post-filter set so the N/M matches exactly what
  // groupBySubmitter renders (it drops uncategorized entries internally).
  const renderableFiltered = filtered.filter(
    (e) => e.entryCategory != null && e.entryCategory !== '',
  );
  const counts = tally(renderableFiltered);
  const visible = filterSelected(renderableFiltered, showSelectedOnly);
  const groups = groupBySubmitter(visible);
  const entryCount = groups.reduce(
    (acc, g) => acc + g.byCategory.reduce((a, c) => a + c.entries.length, 0),
    0,
  );
  const submitterCount = groups.length;
  const includedCount =
    state.kind === 'ok' ? state.entries.filter((e) => e.includeInMbr).length : 0;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Both shells surface the "MCP tokens" link (AOZONE-102): token
          management is self-service for every signed-in user. */}
      <TopBar userEmail={me?.email ?? null} showTokensLink />
      <FilterBar
        state={filters}
        categories={CATEGORY_ORDER}
        submitters={submitters}
        months={monthOptions(now)}
        onChange={setFilters}
        onSearchChange={(q) => setFilters((f) => ({ ...f, search: q }))}
      />
      <TallyBar
        selected={counts.selected}
        total={counts.total}
        showSelectedOnly={showSelectedOnly}
        onToggleSelectedOnly={setShowSelectedOnly}
      />
      <main className="mx-auto max-w-[1280px] px-6 py-8">
        {/* AOZONE-115: admins only — see the `previewing` gate above for why
            this is `=== 'admin'` and not `!== 'contributor'`. */}
        {me?.role === 'admin' && (
          <div className="mb-4 flex justify-end">
            <PreviewContributorBar
              submitters={submitters}
              previewing={null}
              onSelect={enterPreview}
              onExit={exitPreview}
            />
          </div>
        )}
        <PageHeader
          monthLabel={monthLabel}
          entryCount={entryCount}
          submitterCount={submitterCount}
        />
        <GenerateMbrButton
          includedCount={includedCount}
          period={filters.month}
          availableMonths={monthOptions(now)}
        />
        {/* AOZONE-100: MCP token management moved to the dedicated
            /user/tokens page (reached via the user-menu "MCP tokens" link). */}
        {state.kind === 'loading' && <LoadingState />}
        {state.kind === 'error' && (
          <ErrorState message={state.message} onRetry={onRetry} />
        )}
        {state.kind === 'ok' && groups.length === 0 && isDefaultFilterState(filters, now) && (
          <EmptyState monthLabel={monthLabel} />
        )}
        {state.kind === 'ok' && groups.length === 0 && !isDefaultFilterState(filters, now) && (
          <NoMatchState onClearFilters={() => setFilters(defaultFilterState(now))} />
        )}
        {state.kind === 'ok' && groups.length > 0 && (
          <div className="flex flex-col gap-6">
            {groups.map((g) => (
              <SubmitterCard
                key={g.submitterId}
                group={g}
                onToggleInclude={onToggleInclude}
                onSaveDraft={onSaveDraft}
                onSavePeriod={onSavePeriod}
                onRegenerate={onRegenerate}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
